import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById } from '../data/projects';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const project = getProjectById(id);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-slide functionality
  useEffect(() => {
    if (project?.gallery && project.gallery.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === project.gallery.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [project?.gallery]);

  const nextImage = () => {
    if (project?.gallery) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === project.gallery.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (project?.gallery) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? project.gallery.length - 1 : prevIndex - 1
      );
    }
  };

  if (!project) {
    return (
      <div className="bg-white text-slate-800 font-sans min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold mb-4">Project not found</h1>
        <Link to="/portfolio-summary" className="text-[#1e3a8a] underline font-medium">
          Back to Portfolio
        </Link>
      </div>
    );
  }

  const {
    title,
    location,
    completed,
    heroImage,
    description,
    services = [],
    status,
    client,
    size,
    value,
    gallery = [],
  } = project;

  return (
    <div className="bg-white text-slate-800 font-sans antialiased min-h-screen" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>

      {/* Hero Section (Full Width Image) */}
      <div className="relative h-[90vh] w-full overflow-hidden">
        <img 
          src={heroImage || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop'} 
          alt={title} 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-12">
                <span className="text-white/90 text-sm font-medium tracking-widest uppercase mb-2 block">
                  {project.category.join(' / ')}
                </span>
                <h1 className="text-4xl md:text-5xl font-display font-medium text-white uppercase tracking-wide max-w-4xl">
                    {title}
                </h1>
                <div className="flex items-center gap-6 mt-6 text-white/90">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-map-marker-alt text-yellow-500"></i>
                        <span>{location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <i className="far fa-calendar-alt text-yellow-500"></i>
                        <span>Completed {completed}</span>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 ml-10 mr-10" style={{ justifySelf: 'center' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Left Column: Project Description */}
            <div className="lg:col-span-2 space-y-8">
                <div>
                    <h2 className="text-2xl font-display text-gray-900 uppercase tracking-wide mb-6">Project Overview</h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                        {description}
                    </p>
                </div>

                {/* Gallery Slider */}
                <div>
                    <h2 className="text-2xl font-display text-gray-900 uppercase tracking-wide mb-6">Project Gallery</h2>
                    {gallery.length > 0 && (
                      <div className="relative">
                        {/* Single Image Display */}
                        <div className="relative h-96 overflow-hidden">
                          <img 
                            src={gallery[currentImageIndex]} 
                            className="w-full h-full object-cover" 
                            alt={`Gallery ${currentImageIndex + 1}`} 
                          />
                          
                          {/* Navigation Buttons */}
                          {gallery.length > 1 && (
                            <>
                              <button
                                onClick={prevImage}
                                className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#2a3890] hover:bg-white text-white p-3 rounded-full shadow-lg transition-colors"
                                aria-label="Previous image"
                              >
                                <i className="fas fa-chevron-left"></i>
                              </button>
                              <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-[#2a3890] text-gray-800 p-3 rounded-full shadow-lg transition-colors"
                                aria-label="Next image"
                              >
                                <i className="fas fa-chevron-right"></i>
                              </button>
                            </>
                          )}
                        </div>
                        
                        {/* Image Indicators */}
                        {gallery.length > 1 && (
                          <div className="flex justify-center gap-2 mt-4">
                            {gallery.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`h-2 rounded-full transition-all ${
                                  idx === currentImageIndex 
                                    ? 'bg-[#1e3a8a] w-8' 
                                    : 'bg-gray-300 w-2 hover:bg-gray-400'
                                }`}
                                aria-label={`Go to image ${idx + 1}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                </div>
            </div>

            {/* Right Column: Project Details & Context */}
            <div className="lg:col-span-1 space-y-10">     
                {/* Info Card */}
                <div className="bg-gray-50 p-8 border-l-4 border-[#1e3a8a]">
                    <h3 className="text-lg font-display font-medium uppercase tracking-wider text-gray-900 mb-6">Project Specs</h3>
                    <ul className="space-y-4 text-sm">
                        <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Client</span>
                            <span className="font-medium text-gray-900 text-right">{client}</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Location</span>
                            <span className="font-medium text-gray-900">{location}</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Area</span>
                            <span className="font-medium text-gray-900">{size}</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Year</span>
                            <span className="font-medium text-gray-900">{completed}</span>
                        </li>
                        {/* <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Value</span>
                            <span className="font-medium text-gray-900">{value}</span>
                        </li> */}
                        <li className="flex justify-between border-b border-gray-200 pb-2">
                            <span className="text-gray-500">Status</span>
                            <span className="font-medium text-green-600">{status}</span>
                        </li>
                        <li className="flex justify-between pt-1">
                            <span className="text-gray-500">Services</span>
                            <span className="font-medium text-gray-900 text-right">
                              {services.join(', ')}
                            </span>
                        </li>
                    </ul>
                </div>

                {/* Quote / Highlight */}
                <div className="relative">
                    <i className="fas fa-quote-left text-4xl text-gray-200 absolute -top-4 -left-2 -z-10"></i>
                    <p className="text-lg text-gray-700 italic font-light z-10 relative">
                        "Delivering excellence through integrated design-build practices and relentless focus on client outcomes."
                    </p>
                    <p className="mt-4 text-sm font-bold text-gray-900">— Project Lead</p>
                </div>

                {/* CTA Button */}
                {/* <Link
                  to="/portfolio-summary"
                  className="w-full bg-[#1e3a8a] text-white py-4 uppercase tracking-widest text-sm font-medium hover:bg-blue-900 transition flex items-center justify-center gap-2 group"
                >
                  Back to Portfolio
                  <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </Link> */}
            </div>
        </div>
      </main>

      {/* Footer Preview */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-display uppercase tracking-widest mb-4">Ready to build your vision?</h2>
          <button className="border border-white px-8 py-3 hover:bg-white hover:text-black transition uppercase text-sm tracking-wider"
          onClick={() => window.location.href = '/contact'}>
            Contact Us
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ProjectDetailPage;